import { ChangeEvent, useEffect, useState } from "react";
import { Button, Modal, Form, Col, Row, Container } from "react-bootstrap";
import { Gear } from "react-bootstrap-icons";
import { SettingsButtonProps } from "../interfaces/Navbar";
import Select from "./Select";
import { formattedColorName } from "../services/VisServices";

function SettingsButton(props: SettingsButtonProps) {
  //State of the modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  //Local state of the settings UI
  const [newChoreoName, setNewChoreoName] = useState<string>("");
  const [newChoreoDescription, setNewChoreoDescription] = useState<string>("");
  const [newFineGridEnabled, setNewFineGridEnabled] = useState<boolean>(true);
  const [newShowGridIn3D, setNewShowGridIn3D] = useState<boolean>(true);
  const [newGridDragEnabled, setNewGridDragEnabled] = useState<boolean>(true);
  const [newDiscreteHeatmapEnabled, setNewDiscreteHeatmapEnabled] =
    useState<boolean>(true);
  const [newGentlemanColor, setNewGentlemanColor] = useState<
    string | number | readonly string[] | undefined
  >("#0000ff");
  const [newLadyColor, setNewLadyColor] = useState<
    string | number | readonly string[] | undefined
  >("#ff0000");
  const [newCoupleColor, setNewCoupleColor] = useState<
    string | number | readonly string[] | undefined
  >("#4b0082");

  const [newColorCodedCouples, setNewColorCodedCouples] = useState<boolean>(false);
  const [newCoupleColors, setNewCoupleColors] = useState<{id:string,fillColor:string}[]>([]);

  const [newAnimationSpeed, setNewAnimationSpeed] = useState<number>(1);

  function deepCopyCoupleColors() {
    let deepCopy: {id:string,fillColor:string}[] = [];
    newCoupleColors.forEach(entry => {
      let newObj: {id:string,fillColor:string} = {id: entry.id, fillColor: entry.fillColor};
      deepCopy.push(newObj);
    });
    return deepCopy;
  }

  function changeCoupleFill(id: number, newFill: string) {
    let deepCopy = deepCopyCoupleColors();
    deepCopy[id-1].fillColor = newFill;
    setNewCoupleColors(deepCopy);
  }

  function changeCoupleLabel(id: number, newLabel: string) {
    let deepCopy = deepCopyCoupleColors();
    setNewCoupleColors(deepCopy);
  }


  //Toggle functions for all the switches in the UI
  function switchFineGridEnabled(e: any) {
    if (newFineGridEnabled) {
      setNewFineGridEnabled(false);
    } else {
      setNewFineGridEnabled(true);
    }
  }

  function switchShowGridIn3D(e: any) {
    if (newShowGridIn3D) {
      setNewShowGridIn3D(false);
    } else {
      setNewShowGridIn3D(true);
    }
  }

  function switchDiscreteHeatmapEnabled(e: any) {
    if (newDiscreteHeatmapEnabled) {
      setNewDiscreteHeatmapEnabled(false);
    } else {
      setNewDiscreteHeatmapEnabled(true);
    }
  }

  function switchGridDragEnabled(e: any) {
    if (newGridDragEnabled) {
      setNewGridDragEnabled(false);
    } else {
      setNewGridDragEnabled(true);
    }
  }

  function switchColorCodedCouples(e: any) {
    if (newColorCodedCouples) {
      setNewColorCodedCouples(false);
    } else {
      setNewColorCodedCouples(true);
    }
  }

  /**
   * Copy the global state into the UIs local state, so that all UI elements
   * are consistent with the global state
   */
  function openModalWithNewValues() {
    setNewChoreoName(props.choreoName);
    setNewChoreoDescription(props.choreoDescription);
    setNewDiscreteHeatmapEnabled(props.discreteHeatmapEnabled);
    setNewGridDragEnabled(props.gridDragEnabled);
    setNewFineGridEnabled(props.fineGridEnabled);
    setNewGentlemanColor(props.gentlemanColor);
    setNewLadyColor(props.ladyColor);
    setNewCoupleColor(props.coupleColor);
    setNewAnimationSpeed(props.animationSpeed);
    setNewShowGridIn3D(props.showGridIn3D);
    setNewColorCodedCouples(props.colorCodedCouples!);
    
    let buffer:{id:string, fillColor:string}[] = [];
    for (const key in props.coupleColors) {
      if (props.coupleColors.hasOwnProperty(key)) {
        const value = props.coupleColors[key];
        buffer.push({id: key, fillColor: value.fillColor});
      }
    }
    setNewCoupleColors(buffer);

    handleShow();
  }

  /**
   * Updates the "real" state of the program, when the user hits "OK". This means
   * that the local values (marked with "new") will be transferred into the global state.
   * This is done to reduce constant state updates in case the user gets wild in settings.
   */
  function handleCloseWithChange(e: any) {
    if (newChoreoName !== "") {
      props.setChoreoName(newChoreoName);
    }
    if (newChoreoDescription) {
      props.setChoreoDescription(newChoreoDescription);
    }
    props.setGridDragEnabled(newGridDragEnabled);
    props.setFineGridEnabled(newFineGridEnabled);
    props.setDiscreteHeatmapEnabled(newDiscreteHeatmapEnabled);
    props.setGentlemanColor(newGentlemanColor);
    props.setLadyColor(newLadyColor);
    props.setCoupleColor(newCoupleColor);
    props.setAnimationSpeed(newAnimationSpeed);
    props.setTransitionColorScheme(colorSchemes[selectedOption].label);
    props.setShowGridIn3D(newShowGridIn3D);
    props.setColorCodedCouples(newColorCodedCouples);

    const record: Record<number,{fillColor: string}> = {};

    newCoupleColors.forEach((element, index) => {
      record[Number(element.id)] = {fillColor: element.fillColor};
    });
    props.setCoupleColors(record);

    handleClose();
  }

  function changeTransitionColorScheme(e: ChangeEvent<HTMLSelectElement>) {
    setSelectedOption(+e.currentTarget.value);
  }
  const [selectedOption, setSelectedOption] = useState(0);

  const colorSchemes: { value: number; label: string; key: string }[] = [
    { value: 0, label: "RdYlBu", key: "0" },
    { value: 1, label: "RdYlGn", key: "1" },
  ];

  return (
    <>
      <Button
        variant="light"
        className="me-2"
        onClick={openModalWithNewValues}
        title="Settings"
      >
        <Gear /> <br />{" "}
        <p className="not-for-mobile no-margin-bottom">Settings</p>
      </Button>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>

          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Label style={{ fontWeight: "bold" }}>
              Basics
            </Form.Label>

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={newChoreoName}
                onChange={(e) => setNewChoreoName(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                value={newChoreoDescription}
                onChange={(e) => setNewChoreoDescription(e.target.value)}
                as="textarea"
                rows={3}
              />
            </Form.Group>


            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>

            <Form.Label style={{ fontWeight: "bold" }}>
              Visualization Settings
            </Form.Label>
            

              <Form.Check
                type="switch"
                id="fineGridSwitch"
                label="Show fine grid"
                checked={newFineGridEnabled}
                onChange={switchFineGridEnabled}
              />
            </Form.Group>

            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
              <Form.Check
                type="switch"
                id="3dGridSwitch"
                label="Show grid in 3D"
                checked={newShowGridIn3D}
                onChange={switchShowGridIn3D}
              />
            </Form.Group>

            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
              <Form.Check
                type="switch"
                id="gridDragSwitch"
                label="Enable drag and drop on grid"
                checked={newGridDragEnabled}
                onChange={switchGridDragEnabled}
              />
            </Form.Group>


            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>

              <Form.Check
                type="switch"
                id="heatmapSwitch"
                label={
                  newDiscreteHeatmapEnabled
                    ? "Discrete Heatmap"
                    : "Continuous Heatmap"
                }
                checked={newDiscreteHeatmapEnabled}
                onChange={switchDiscreteHeatmapEnabled}
              />
            </Form.Group>

            <Form.Label>Color scheme for transitions:</Form.Label>
            <Select
              value={selectedOption}
              onChange={changeTransitionColorScheme}
              options={colorSchemes}
              renderOnAllDevices={true}
            />


            <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
              <Form.Check
                type="switch"
                id="dancerColorSwitch"
                label={
                  newColorCodedCouples
                    ? "Color coded couples"
                    : "Default colors"
                }
                checked={newColorCodedCouples}
                onChange={switchColorCodedCouples}

              />
            </Form.Group>
            
            {!newColorCodedCouples && (
              <div>
                <Form.Label style={{ fontWeight: "bold" }}>
                  Default colors
                </Form.Label>
                <Container>
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Gentleman</Form.Label>
                        <Form.Control
                          type="color"
                          id="exampleColorInput"
                          defaultValue={newGentlemanColor}
                          onChange={(e) => setNewGentlemanColor(e.target.value)}
                        />
                      </Form.Group>
                    </Col>


                    <Col>
                      <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
                        <Form.Label>Lady</Form.Label>
                        <Form.Control
                          type="color"
                          id="exampleColorInput"
                          defaultValue={newLadyColor}
                          onChange={(e) => setNewLadyColor(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3" onSubmit={(e) => e.preventDefault()}>
                        <Form.Label>Color</Form.Label>
                        <Form.Control
                          type="color"
                          id="exampleColorInput"
                          defaultValue={newCoupleColor}
                          onChange={(e) => setNewCoupleColor(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </div>
            )}
            
            {newColorCodedCouples && (
              <div>
                <Form.Label style={{ fontWeight: "bold" }}>
                  Color coded couples
                </Form.Label>

                <Container>
                  {newCoupleColors.map(couple=> (
                    <Row>
                      <Col sm={1}><Form.Label>{couple.id}</Form.Label></Col>
                      <Col sm={2}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="color"
                            defaultValue={couple.fillColor}
                            onChange={(e) => changeCoupleFill(Number(couple.id), e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Label>{formattedColorName(couple.fillColor)}</Form.Label>
                      </Col>
                    </Row>
                  ))}
                </Container>
              </div>
            )}


            <Form.Group className="mb-3" as={Row} onSubmit={(e) => e.preventDefault()}>
              <Form.Label>Animation Speed</Form.Label>
              <Col xs="11">
                <Form.Range
                  value={newAnimationSpeed}
                  onChange={(e) => setNewAnimationSpeed(+e.target.value)}
                  min={0.5}
                  max={2}
                  step={0.1}
                />
              </Col>
              <Col xs="1">
                <Form.Label>{newAnimationSpeed}</Form.Label>
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseWithChange}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SettingsButton;
